import type { PlayerSliceDeps } from "../deps";
import type {
  PlayerStore,
  QueueState,
  QueueActions,
  NamedSlice,
} from "../types";
import type { CatalogCollectionType, CatalogSong } from "../../domain/types";
import type { PlayerQueueType, QueueItem } from "../../utils/queue.types";
import {
  getNextIndex,
  getPrevIndex,
  shouldTrimOnRepeatAll,
  playlistScopeEnd,
  shuffleList,
  shouldFetchMoreForYouRecommendations,
  shouldFetchMorePlaylistRecommendations,
  isPlayableSong,
} from "../../utils/queue.utils";
import { resolvePlaybackResumePositionMs } from "../../features/resume/resumeCheckpoint";

export const createQueueSlice = (
  deps: PlayerSliceDeps,
): NamedSlice<QueueState & { actions: QueueActions }, PlayerStore> => {
  const { playLifecycle } = deps;
  const {
    fetchSongsForCollection,
    fetchSongById,
    fetchArtistPopularSongs,
    tryGetSongsFromCache,
    substituteLocalSongAssets,
  } = deps.catalog;
  const {
    getConfirmedProgressPositionForQueueItem,
    ensureProgressForPlaybackTarget,
    progressState,
    resetProgressState,
    seedProgressForPlaybackTarget,
  } = deps.progress;
  const {
    clearPlaybackResumeCheckpoint,
    readPlaybackResumeCheckpoint,
    writePlaybackResumeCheckpoint,
  } = deps.resume;
  const getQueueItemForType = (
    state: PlayerStore,
    queueType: PlayerQueueType,
  ): QueueItem | null => {
    const queue =
      queueType === "playlist" ? state.playlistQueue : state.foryouQueue;
    return queue && queue.currentIndex >= 0
      ? (queue.items[queue.currentIndex] ?? null)
      : null;
  };

  const persistActiveQueueProgress = (state: PlayerStore): void => {
    const item = getQueueItemForType(state, state.activeQueueType);
    if (!item) return;

    const positionSeconds = getConfirmedProgressPositionForQueueItem(item.qid);
    if (positionSeconds == null || !Number.isFinite(positionSeconds)) return;

    writePlaybackResumeCheckpoint({
      queueType: state.activeQueueType,
      songId: item.metadata.id,
      queueItemId: item.qid,
      positionMs: positionSeconds * 1000,
      durationMs:
        progressState.duration.value > 0
          ? progressState.duration.value * 1000
          : (item.metadata.duration ?? 0) * 1000,
    });
  };

  const resolveQueueResumePositionMs = (
    queueType: PlayerQueueType,
    item: QueueItem,
  ): number | null => {
    const checkpoint = readPlaybackResumeCheckpoint(queueType);
    if (!checkpoint) return null;

    const positionMs = resolvePlaybackResumePositionMs(checkpoint, {
      songId: item.metadata.id,
      queueItemId: item.qid,
      durationMs: (item.metadata.duration ?? 0) * 1000,
    });
    if (positionMs == null) {
      clearPlaybackResumeCheckpoint(queueType);
    }
    return positionMs;
  };

  const prepareQueueSwitchProgress = (
    state: PlayerStore,
    queueType: PlayerQueueType,
    item: QueueItem | null,
  ) => {
    if (!item) {
      resetProgressState();
      return null;
    }

    if (state.activeQueueType === queueType) {
      ensureProgressForPlaybackTarget(item);
      return null;
    }

    const positionMs = resolveQueueResumePositionMs(queueType, item);
    seedProgressForPlaybackTarget(item, positionMs ?? 0);
    return positionMs == null
      ? null
      : {
          songId: item.metadata.id,
          queueItemId: item.qid,
          positionMs,
        };
  };

  const inferCollectionName = (
    type: CatalogCollectionType,
    collectionId: string,
    songs: CatalogSong[],
  ): string | undefined => {
    if (type === "album") {
      return songs
        .flatMap((song) => song.albums ?? [])
        .find((album) => album.id === collectionId)?.title;
    }

    if (type === "artist") {
      return songs
        .flatMap((song) => song.artists ?? [])
        .find((artist) => artist.id === collectionId)?.name;
    }

    return undefined;
  };

  return (set, get) => ({
    playlistQueue: null,
    foryouQueue: null,
    activeQueueType: "foryou",

    actions: {
      setActiveQueueType: (type) => {
        const targetState = get();
        const isSwitchingQueue = targetState.activeQueueType !== type;
        if (isSwitchingQueue) {
          persistActiveQueueProgress(targetState);
        }
        const targetItem = getQueueItemForType(targetState, type);
        const pendingSeek = prepareQueueSwitchProgress(
          targetState,
          type,
          targetItem,
        );

        set(
          (state) => {
            state.activeQueueType = type;
            state.pendingSeek = pendingSeek;
          },
          false,
          "queue/setActiveQueueType",
        );

        if (type === "playlist") {
          const playlistQueue = get().playlistQueue;
          if (!playlistQueue || playlistQueue.items.length === 0) {
            console.audio.warn(
              "queue/setActiveQueueType: playlist empty, pausing",
            );
            if (get().status === "PLAYING") {
              get().actions.pause().catch(console.error);
            }
            return;
          }
        }

        get().actions.play().catch(console.error);
      },

      next: () => {
        let didAdvance = false;
        const before = get();
        const current =
          before.activeQueueType === "playlist"
            ? before.playlistQueue
            : before.foryouQueue;
        if (current) {
          const length =
            before.activeQueueType === "playlist" &&
            before.repeatMode === "queue"
              ? playlistScopeEnd(current) + 1
              : current.items.length;
          const next = getNextIndex(
            current.currentIndex,
            length,
            before.repeatMode,
          );
          if (next === null) playLifecycle.end("queue_end");
          else if (next === current.currentIndex)
            playLifecycle.end("track_change");
        }

        set(
          (state: any) => {
            const queueType = state.activeQueueType;
            const queue =
              queueType === "playlist"
                ? state.playlistQueue
                : state.foryouQueue;

            if (!queue) {
              console.audio.warn("queue/next: no active queue");
              return;
            }
            const { currentIndex, items } = queue;
            const repeatMode = state.repeatMode;

            // Repeat-all loops the COMMITTED scope (original collection + every
            // recommendation reached — playlistScopeEnd), not the whole list:
            // trailing not-yet-reached recommendations stay outside the loop.
            // With repeat off the whole flat list plays through — reaching a
            // recommendation is what commits it (high-water update below).
            const wrapLength =
              queueType === "playlist" && repeatMode === "queue"
                ? playlistScopeEnd(queue) + 1
                : items.length;
            const nextIndex = getNextIndex(
              currentIndex,
              wrapLength,
              repeatMode,
            );

            if (nextIndex !== null) {
              seedProgressForPlaybackTarget(items[nextIndex]);
              queue.currentIndex = nextIndex;
              if (queueType === "playlist") {
                queue.highWaterIndex = Math.max(
                  queue.highWaterIndex ?? -1,
                  nextIndex,
                );
              }
              state.pendingSeek = null;
              // Set userIntent to playing when switching songs (autoplay behavior)
              state.userIntent = "playing";
              didAdvance = true;
              if (queueType === "foryou") {
                // Set directly on state instead of calling action from within Immer draft
                state.lastViewedSongId = items[nextIndex].metadata.id;
              }
            } else {
              // No next track — stop playback instead of replaying current song
              state.userIntent = "paused";
            }

            // Infinite scroll logic - only fetch more when repeat is off
            if (repeatMode === "off") {
              if (
                queueType === "foryou" &&
                nextIndex !== null &&
                shouldFetchMoreForYouRecommendations(nextIndex, items.length)
              ) {
                // Defer prefetch work until after interactions to keep swipes smooth.
                deps.afterInteractions(() => {
                  get().actions.fetchMoreRecommendations().catch(console.error);
                });
              }

              if (
                queueType === "playlist" &&
                nextIndex !== null &&
                shouldFetchMorePlaylistRecommendations(nextIndex, items.length)
              ) {
                deps.afterInteractions(() => {
                  get()
                    .actions.fetchMorePlaylistRecommendations()
                    .catch(console.error);
                });
              }
            }
          },
          false,
          "queue/next",
        );

        if (didAdvance && get().userIntent === "playing") {
          get().actions.play().catch(console.error);
        }
      },

      prev: () => {
        const before = get();
        const current =
          before.activeQueueType === "playlist"
            ? before.playlistQueue
            : before.foryouQueue;
        if (current) {
          const length =
            before.activeQueueType === "playlist" &&
            before.repeatMode === "queue"
              ? playlistScopeEnd(current) + 1
              : current.items.length;
          if (
            getPrevIndex(current.currentIndex, length, before.repeatMode) ===
            current.currentIndex
          )
            playLifecycle.end("track_change");
        }
        set(
          (state: any) => {
            const queueType = state.activeQueueType;
            const queue =
              queueType === "playlist"
                ? state.playlistQueue
                : state.foryouQueue;

            if (!queue) {
              console.audio.warn("queue/prev: no active queue");
              return;
            }
            const { currentIndex, items } = queue;
            const repeatMode = state.repeatMode;

            // Mirror next(): repeat-all wraps within the committed scope.
            const wrapLength =
              queueType === "playlist" && repeatMode === "queue"
                ? playlistScopeEnd(queue) + 1
                : items.length;
            const prevIndex = getPrevIndex(
              currentIndex,
              wrapLength,
              repeatMode,
            );

            if (prevIndex !== null) {
              seedProgressForPlaybackTarget(items[prevIndex]);
              queue.currentIndex = prevIndex;
              state.pendingSeek = null;
              // Set userIntent to playing when switching songs (autoplay behavior)
              state.userIntent = "playing";
              if (queueType === "foryou") {
                // Set directly on state instead of calling action from within Immer draft
                state.lastViewedSongId = items[prevIndex].metadata.id;
              }
            }
          },
          false,
          "queue/prev",
        );

        if (get().userIntent === "playing") {
          get().actions.play().catch(console.error);
        }
      },

      jumpTo: (qid: string, options?: { autoPlay?: boolean }) => {
        const current =
          get().activeQueueType === "playlist"
            ? get().playlistQueue
            : get().foryouQueue;
        if (current?.items[current.currentIndex]?.qid === qid)
          playLifecycle.end("track_change");
        set(
          (state: any) => {
            const queueType = state.activeQueueType;
            const queue =
              queueType === "playlist"
                ? state.playlistQueue
                : state.foryouQueue;

            if (!queue) {
              console.audio.warn("queue/jumpTo: no active queue", { qid });
              return;
            }
            const index = queue.items.findIndex((i: any) => i.qid === qid);

            if (index !== -1) {
              seedProgressForPlaybackTarget(queue.items[index]);
              queue.currentIndex = index;
              // An explicit jump commits everything up to the target — the
              // high-water mark only moves forward (continuous-listening model).
              if (queueType === "playlist") {
                queue.highWaterIndex = Math.max(
                  queue.highWaterIndex ?? -1,
                  index,
                );
              }
              state.pendingSeek = null;
              // Swiping / explicit jump implies playing unless explicitly paused AND caller disables autoplay.
              const wantsAutoPlay = options?.autoPlay !== false;
              if (wantsAutoPlay || state.userIntent === "playing") {
                state.userIntent = "playing";
              }
              if (queueType === "foryou") {
                // Set directly on state instead of calling action from within Immer draft
                state.lastViewedSongId = queue.items[index].metadata.id;
              }

              // Infinite scroll logic - only fetch more when repeat is off
              if (state.repeatMode === "off") {
                if (
                  queueType === "foryou" &&
                  shouldFetchMoreForYouRecommendations(
                    index,
                    queue.items.length,
                  )
                ) {
                  deps.afterInteractions(() => {
                    get()
                      .actions.fetchMoreRecommendations()
                      .catch(console.error);
                  });
                }

                if (
                  queueType === "playlist" &&
                  shouldFetchMorePlaylistRecommendations(
                    index,
                    queue.items.length,
                  )
                ) {
                  deps.afterInteractions(() => {
                    get()
                      .actions.fetchMorePlaylistRecommendations()
                      .catch(console.error);
                  });
                }
              }
            }
          },
          false,
          "queue/jumpTo",
        );

        if (get().userIntent === "playing") {
          get().actions.play().catch(console.error);
        }
      },

      toggleShuffle: () => {
        set(
          (state: any) => {
            const queue = state.playlistQueue; // Only playlist supports shuffle
            if (!queue) return;

            const { shuffleState, items, currentIndex } = queue;
            const currentItem = items[currentIndex];
            if (!currentItem) return;

            if (!shuffleState.isOn) {
              // Turn ON (continuous-listening model, 2026-08-02): shuffle the
              // COMMITTED scope only — the original collection plus every
              // recommendation reached (playlistScopeEnd). Not-yet-reached
              // recommendations trail after the shuffled region in their
              // original order. Sources are otherwise indistinguishable —
              // a reached recommendation shuffles like any album track.
              queue.shuffleState.originalOrder = [...items];
              queue.shuffleState.isOn = true;

              const scopeEnd = playlistScopeEnd(queue);
              const scoped = items.slice(0, scopeEnd + 1);
              const trailing = items.slice(scopeEnd + 1);
              const scopedIndex = scoped.findIndex(
                (item: QueueItem) => item.qid === currentItem.qid,
              );
              queue.items = [
                ...shuffleList(
                  scoped,
                  scopedIndex === -1 ? undefined : scopedIndex,
                ),
                ...trailing,
              ];
              // shuffleList moves the current item to the front.
              queue.currentIndex = scopedIndex === -1 ? queue.currentIndex : 0;
            } else {
              // Turn OFF: restore the pre-shuffle order exactly (the snapshot,
              // plus anything appended since — appends also land on the
              // snapshot). Nothing is filtered by source.
              if (shuffleState.originalOrder) {
                const originalOrder = shuffleState.originalOrder;
                queue.items = originalOrder;
                queue.currentIndex = originalOrder.findIndex(
                  (i: any) => i.qid === currentItem.qid,
                );
                queue.shuffleState.originalOrder = null;
              }
              queue.shuffleState.isOn = false;
            }
          },
          false,
          "queue/toggleShuffle",
        );
      },

      setRepeatMode: (mode: any) => {
        const previousMode = get().repeatMode;
        set(
          (state: any) => {
            state.repeatMode = mode;

            // Continuous-listening model (2026-08-02): repeat-all on a PLAYLIST
            // queue is non-destructive — the loop boundary is computed at
            // advance time (next()/prev() via playlistScopeEnd), so nothing is
            // trimmed here and turning repeat off later needs no restore. Only
            // the foryou queue (an endless recommendation stream) still trims
            // to a bounded window when repeat-all engages.
            if (mode === "queue" && previousMode !== "queue") {
              const queueType = state.activeQueueType;
              const queue =
                queueType === "playlist"
                  ? state.playlistQueue
                  : state.foryouQueue;
              if (!queue) return;
              if (queueType === "playlist") return;

              const boundary = shouldTrimOnRepeatAll({
                queueType,
                currentIndex: queue.currentIndex,
                queueLength: queue.items.length,
                isShuffleOn: false,
                originalCollectionLength: undefined,
              });
              if (boundary !== null) {
                queue.items = queue.items.slice(0, boundary);
              }
            }
          },
          false,
          "queue/setRepeatMode",
        );

        // When turning repeat OFF, check if we need to fetch more recommendations
        if (previousMode !== "off" && mode === "off") {
          const state = get();
          const queueType = state.activeQueueType;
          const queue =
            queueType === "playlist" ? state.playlistQueue : state.foryouQueue;
          if (!queue) return;
          const { currentIndex, items } = queue;

          if (
            queueType === "foryou" &&
            shouldFetchMoreForYouRecommendations(currentIndex, items.length)
          ) {
            state.actions.fetchMoreRecommendations().catch(console.error);
          }
          if (
            queueType === "playlist" &&
            shouldFetchMorePlaylistRecommendations(currentIndex, items.length)
          ) {
            state.actions
              .fetchMorePlaylistRecommendations()
              .catch(console.error);
          }
        }
      },

      playCollection: async (
        collectionId: string,
        type: any,
        startSongId?: string,
        songs?: any[],
        collectionName?: string,
      ) => {
        try {
          // Three-tier song resolution:
          // 1. Songs passed directly (from UI components that already have the data)
          // 2. React Query cache hit (data from a previous UI render)
          // 3. Network fetch (fallback for deep links, search results, etc.)
          //
          // Tiers 2 & 3 already substitute local audio paths internally
          // (see `tryGetSongsFromCache` and `fetchSongsForCollection`). Tier
          // 1 — songs handed in by screen-level components like the playlist
          // screen — carries remote URLs because the screen-level hooks
          // (`usePlaylist`, `useAlbum`) are server-truth on list membership
          // but don't field-substitute audio paths. Without this wrap, an
          // offline tap-to-play on a downloaded playlist hands the player
          // remote URLs and fails.
          const rawSongs =
            songs ??
            tryGetSongsFromCache(collectionId, type) ??
            (type === "artist"
              ? await fetchArtistPopularSongs(collectionId)
              : await fetchSongsForCollection({
                  collectionType: type,
                  collectionId: collectionId,
                }));
          // Idempotent: substituteLocalSongAssets on already-substituted
          // songs is a no-op (file:// URLs aren't in cached_tracks anyway).
          const resolvedSongs = substituteLocalSongAssets(rawSongs);

          // Filter out external (unmatched) songs — they have no playable audio
          const playableSongs = resolvedSongs.filter((song: any) =>
            isPlayableSong(song),
          );

          const items: QueueItem[] = playableSongs.map((song: any) => ({
            qid: `${song.id}-${Math.random().toString(36).substr(2, 9)}`,
            metadata: song,
            source: "collection",
          }));

          const startIndex = startSongId
            ? items.findIndex((i: QueueItem) => i.metadata.id === startSongId)
            : 0;
          if (startSongId && startIndex === -1) {
            console.audio.warn("queue/playCollection: start song unavailable", {
              collectionId,
              startSongId,
              type,
            });
            return false;
          }
          const finalCurrentIndex = startIndex;
          const shouldShuffle = get().shuffleEnabled && items.length > 1;
          const queueItems = shouldShuffle
            ? shuffleList(items, finalCurrentIndex)
            : items;
          const resolvedCollectionName =
            collectionName ??
            inferCollectionName(type, collectionId, playableSongs);

          const playbackTarget =
            queueItems[shouldShuffle ? 0 : finalCurrentIndex];
          if (playbackTarget) {
            seedProgressForPlaybackTarget(playbackTarget);
          } else {
            resetProgressState();
          }

          set(
            (state) => {
              state.playlistQueue = {
                type: "playlist",
                collection: {
                  type,
                  id: collectionId,
                  name: resolvedCollectionName,
                },
                items: queueItems,
                currentIndex: shouldShuffle ? 0 : finalCurrentIndex,
                shuffleState: {
                  isOn: shouldShuffle,
                  originalOrder: shouldShuffle ? items : null,
                },
              };
              state.activeQueueType = "playlist";
              // Reset recommendation cursor for the new collection
              state.playlistRecsCursorSongId = null;
              // Set userIntent to playing when starting a new collection
              state.userIntent = "playing";
              state.pendingSeek = null;
            },
            false,
            "queue/playCollection",
          );

          // Bootstrap playlist recommendations so repeat-off autoplay is ready,
          // but keep them hidden until the playlist actually reaches its end.
          get().actions.fetchMorePlaylistRecommendations().catch(console.error);
          get().actions.play().catch(console.error);
          return true;
        } catch (e) {
          console.audio.error("queue/playCollection: failed to load", {
            collectionId,
            type,
            error: (e as Error).message,
          });
          return false;
        }
      },

      playForYou: async () => {
        const initialState = get();
        const isSwitchingQueue = initialState.activeQueueType !== "foryou";
        if (isSwitchingQueue) {
          persistActiveQueueProgress(initialState);
        }
        const existingItem = getQueueItemForType(initialState, "foryou");
        const pendingSeek = prepareQueueSwitchProgress(
          initialState,
          "foryou",
          existingItem,
        );

        set(
          (state) => {
            state.activeQueueType = "foryou";
            // Set userIntent to playing when starting For You
            state.userIntent = "playing";
            state.pendingSeek = pendingSeek;
          },
          false,
          "queue/playForYou/setType",
        );

        const state = get();
        if (!state.foryouQueue || state.foryouQueue.items.length === 0) {
          await state.actions.loadForYouInitial();
          const loadedState = get();
          const loadedItem = getQueueItemForType(loadedState, "foryou");
          const loadedPositionMs = loadedItem
            ? resolveQueueResumePositionMs("foryou", loadedItem)
            : null;
          if (loadedItem) {
            seedProgressForPlaybackTarget(loadedItem, loadedPositionMs ?? 0);
          } else {
            resetProgressState();
          }
          set(
            (latestState) => {
              latestState.pendingSeek =
                loadedItem && loadedPositionMs != null
                  ? {
                      songId: loadedItem.metadata.id,
                      queueItemId: loadedItem.qid,
                      positionMs: loadedPositionMs,
                    }
                  : null;
            },
            false,
            "queue/playForYou/restoreProgress",
          );
        }
        get().actions.play().catch(console.error);
      },

      addNext: async (songId: string) => {
        const song = await fetchSongById(songId);
        if (!song) {
          console.audio.warn("queue/addNext: song not found", { songId });
          return;
        }
        // Defense in depth: external songs have no playable audio — inserting
        // one would make the player throw when playback reaches it. UI entry
        // points hide "Add to Queue" for these, but guard here too so no
        // caller can enqueue an unplayable song.
        if (!isPlayableSong(song)) {
          console.audio.warn("queue/addNext: song not playable, skipping", {
            songId,
            status: song.status,
          });
          return;
        }

        set(
          (state) => {
            const queue =
              state.activeQueueType === "playlist"
                ? state.playlistQueue
                : state.foryouQueue;

            if (!queue) {
              console.audio.warn("queue/addNext: no active queue");
              return;
            }
            const newItem: QueueItem = {
              qid: `${song.id}-${Math.random().toString(36).substr(2, 9)}`,
              metadata: song,
              source: "manual",
            };
            const insertAt = queue.currentIndex + 1;
            queue.items.splice(insertAt, 0, newItem);
          },
          false,
          "queue/addNext",
        );
      },

      syncPlaylistQueue: (songs) => {
        set(
          (state) => {
            const queue = state.playlistQueue;
            if (!queue) return;

            const existingIds = new Set(
              queue.items.map((item) => item.metadata.id),
            );
            const newItems: QueueItem[] = songs
              .filter(
                (song) => !existingIds.has(song.id) && isPlayableSong(song),
              )
              .map((song) => ({
                qid: `${song.id}-${Math.random().toString(36).substr(2, 9)}`,
                metadata: song,
                source: "collection" as const,
              }));

            if (newItems.length > 0) {
              queue.items = [...queue.items, ...newItems];
              if (queue.shuffleState.originalOrder) {
                queue.shuffleState.originalOrder = [
                  ...queue.shuffleState.originalOrder,
                  ...newItems,
                ];
              }
            }
          },
          false,
          "queue/syncPlaylistQueue",
        );
      },

      removeFromQueue: (queueItemIds: string[]) => {
        set(
          (state: any) => {
            const queue =
              state.activeQueueType === "playlist"
                ? state.playlistQueue
                : state.foryouQueue;

            if (!queue) return;
            const idsToRemove = new Set(queueItemIds);
            const currentQid = queue.items[queue.currentIndex]?.qid;

            queue.items = queue.items.filter(
              (item: any) => !idsToRemove.has(item.qid),
            );
            // Keep the committed-scope boundary inside the shrunken list.
            if (queue.highWaterIndex !== undefined) {
              queue.highWaterIndex = Math.min(
                queue.highWaterIndex,
                queue.items.length - 1,
              );
            }

            if (queue.shuffleState?.originalOrder) {
              queue.shuffleState.originalOrder =
                queue.shuffleState.originalOrder.filter(
                  (item: QueueItem) => !idsToRemove.has(item.qid),
                );
            }

            // Update currentIndex
            if (currentQid) {
              queue.currentIndex = queue.items.findIndex(
                (item: any) => item.qid === currentQid,
              );
            }

            if (queue.currentIndex === -1 && queue.items.length > 0) {
              queue.currentIndex = 0;
            }

            const nextCurrentItem = queue.items[queue.currentIndex];
            if (nextCurrentItem) {
              ensureProgressForPlaybackTarget(nextCurrentItem);
            } else {
              resetProgressState();
            }
          },
          false,
          "queue/removeFromQueue",
        );
      },

      setUpcomingQueueItemIds: (queueItemIds: string[]) => {
        set(
          (state: any) => {
            const queue =
              state.activeQueueType === "playlist"
                ? state.playlistQueue
                : state.foryouQueue;

            if (!queue) return;
            const { items, currentIndex } = queue;
            const playedItems = items.slice(0, currentIndex + 1);
            const upcomingItemsMap = new Map(
              items.slice(currentIndex + 1).map((i: any) => [i.qid, i]),
            );

            const nextUpcoming = queueItemIds
              .map((qid) => upcomingItemsMap.get(qid))
              .filter((i): i is any => !!i);

            queue.items = [...playedItems, ...nextUpcoming];
          },
          false,
          "queue/setUpcomingQueueItemIds",
        );
      },
    },
  });
};
